import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import UserAvatar from "@/components/UserAvatar";
import api from "@/lib/api";
import {
  GraduationCap, X, ChevronDown, ChevronRight, Palette,
  LayoutDashboard, Building2, ClipboardCheck, Settings, BookOpen,
  UserCog, Briefcase, GraduationCap as Grad, Package, CalendarDays,
  Receipt, KeyRound, ScrollText, LifeBuoy, Building, School,
  Users, MinusCircle, CalendarCheck, Wallet, Calculator, FileText,
  ClipboardList, History, MessageSquare, ShieldCheck, Bell, Megaphone, ShieldAlert, Target, Crown, CreditCard, Brain, Printer, Send, Inbox, ScanLine, QrCode, Trophy,
} from "lucide-react";

const EMPLOYEES_CHILDREN = [
  { to: "/employees/list", label: "Employee List", icon: Users },
  { to: "/employees/other-deductions", label: "Employee Other Deductions", icon: MinusCircle },
  { to: "/employees/attendance", label: "Employee Attendance", icon: CalendarCheck },
  { to: "/employees/salary-module", label: "Employee Salary Module", icon: Wallet },
  { to: "/employees/salary-formula", label: "Employee Salary Formula", icon: Calculator },
  { to: "/employees/payslip", label: "Pay Slip", icon: FileText },
  { to: "/employees/payslip-overview", label: "Pay Slip Overview", icon: ClipboardList },
  { to: "/employees/payslip-history", label: "Pay Slip History", icon: History },
];

const STUDENTS_CHILDREN = [
  { to: "/students", label: "Student List", icon: Users },
  { to: "/students/pending", label: "Pending Enrollments", icon: ClipboardCheck },
  { to: "/students/profile-updates", label: "Profile Updates", icon: UserCog },
  { to: "/students/accounts", label: "Student Accounts", icon: Receipt },
  { to: "/students/accounts/statement-of-account", label: "Statement of Account", icon: FileText },
  { to: "/students/program-assessment", label: "Program Assessment", icon: GraduationCap },
  { to: "/students/retention-fee", label: "Retention Fee", icon: Wallet },
  { to: "/students/attendance", label: "Class Attendance", icon: CalendarCheck },
  { to: "/students/individual-attendance", label: "Individual Class Attendance", icon: CalendarCheck },
  { to: "/attendance/scan", label: "Scan QR Attendance", icon: ScanLine },
  { to: "/id-cards", label: "ID Cards (QR)", icon: QrCode },
  { to: "/students/grading-setup", label: "Grading Setup", icon: BookOpen },
  { to: "/students/grades", label: "Grades", icon: ClipboardList },
  { to: "/students/report-cards", label: "Report Cards", icon: FileText },
  { to: "/students/rankings", label: "Class Rankings", icon: Trophy },
  { to: "/students/progress-feedback", label: "Progress Feedback", icon: MessageSquare },
  { to: "/students/tuition-plans", label: "Tuition Plans", icon: Wallet },
];

const EMPLOYEES_CHILDREN_FOR_STAFF = (perms) => {
  const items = [];
  if (perms.employees_view) items.push({ to: "/employees/list", label: "Employee List", icon: Users });
  if (perms.employee_deductions_view) items.push({ to: "/employees/other-deductions", label: "Employee Other Deductions", icon: MinusCircle });
  if (perms.employee_attendance_view) items.push({ to: "/employees/attendance", label: "Employee Attendance", icon: CalendarCheck });
  if (perms.salary_view) items.push({ to: "/employees/salary-module", label: "Employee Salary Module", icon: Wallet });
  if (perms.salary_formula_view) items.push({ to: "/employees/salary-formula", label: "Employee Salary Formula", icon: Calculator });
  if (perms.payslip_view) items.push({ to: "/employees/payslip", label: "Pay Slip", icon: FileText });
  if (perms.payslip_overview_view) items.push({ to: "/employees/payslip-overview", label: "Pay Slip Overview", icon: ClipboardList });
  if (perms.payslip_history_view) items.push({ to: "/employees/payslip-history", label: "Pay Slip History", icon: History });
  return items;
};

const STUDENTS_CHILDREN_FOR_STAFF = (perms) => {
  const items = [];
  if (perms.students_view) {
    items.push({ to: "/students", label: "Student List", icon: Users });
    items.push({ to: "/students/pending", label: "Pending Enrollments", icon: ClipboardCheck });
    items.push({ to: "/students/profile-updates", label: "Profile Updates", icon: UserCog });
  }
  if (perms.student_accounts_view) {
    items.push({ to: "/students/accounts", label: "Student Accounts", icon: Receipt });
  }
  if (perms.statement_of_account_view) {
    items.push({ to: "/students/accounts/statement-of-account", label: "Statement of Account", icon: FileText });
  }
  if (perms.program_assessment_view) {
    items.push({ to: "/students/program-assessment", label: "Program Assessment", icon: GraduationCap });
  }
  if (perms.retention_fee_view) {
    items.push({ to: "/students/retention-fee", label: "Retention Fee", icon: Wallet });
  }
  if (perms.attendance_view || perms.attendance_mark) {
    items.push({ to: "/students/attendance", label: "Class Attendance", icon: CalendarCheck });
    items.push({ to: "/students/individual-attendance", label: "Individual Class Attendance", icon: CalendarCheck });
  }
  if (perms.attendance_mark) {
    items.push({ to: "/attendance/scan", label: "Scan QR Attendance", icon: ScanLine });
  }
  if (perms.students_view) {
    items.push({ to: "/id-cards", label: "ID Cards (QR)", icon: QrCode });
  }
  if (perms.grading_setup_view) {
    items.push({ to: "/students/grading-setup", label: "Grading Setup", icon: BookOpen });
  }
  if (perms.grades_view) {
    items.push({ to: "/students/grades", label: "Grades", icon: ClipboardList });
  }
  if (perms.report_cards_view) {
    items.push({ to: "/students/report-cards", label: "Report Cards", icon: FileText });
    items.push({ to: "/students/rankings", label: "Class Rankings", icon: Trophy });
  }
  if (perms.progress_feedback_view) {
    items.push({ to: "/students/progress-feedback", label: "Progress Feedback", icon: MessageSquare });
  }
  if (perms.tuition_view || perms.tuition_record_payment || perms.tuition_manage_plan) {
    items.push({ to: "/students/tuition-plans", label: "Tuition Plans", icon: Wallet });
  }
  return items;
};

const ACCOUNTING_CHILDREN_FOR_STAFF = (perms) => {
  const items = [];
  if (perms.accounting_income_view) items.push({ to: "/accounting/income", label: "Income", icon: ClipboardList });
  if (perms.accounting_expenses_view) items.push({ to: "/accounting/expenses", label: "Expenses", icon: MinusCircle });
  if (perms.reports_view) items.push({ to: "/accounting/reports", label: "Reports", icon: FileText });
  if (perms.cash_analysis_view) items.push({ to: "/accounting/cash-analysis", label: "Cash Analysis", icon: Wallet });
  if (perms.accounting_income_view) items.push({ to: "/accounting/daily-cash-report", label: "Daily Cash Report", icon: Printer });
  return items;
};

export function buildStaffItems(perms) {
  const items = [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }];
  if (perms.branch_documents_view) {
    items.push({ to: "/documents", label: "Documents", icon: FileText });
  }
  if (perms.inquiries_view) {
    items.push({ to: "/inquiries", label: "Inquiries", icon: Inbox });
  }
  const studentsChildren = STUDENTS_CHILDREN_FOR_STAFF(perms);
  if (studentsChildren.length > 0) {
    items.push({ label: "Students", icon: Grad, basePath: "/students", children: studentsChildren });
  }
  if (perms.classes_view) {
    items.push({ to: "/classes", label: "Classes & Sections", icon: BookOpen });
  }
  if (perms.employees_view) {
    const empChildren = EMPLOYEES_CHILDREN_FOR_STAFF(perms);
    if (empChildren.length > 1) {
      items.push({ label: "Employees", icon: Briefcase, basePath: "/employees", children: empChildren });
    } else {
      // only Employee List visible — keep as a single leaf to match the old behaviour
      items.push({ to: "/employees/list", label: "Employees", icon: Briefcase });
    }
  } else {
    // employee_view not granted but staff still has access to sub-modules below it
    const empChildren = EMPLOYEES_CHILDREN_FOR_STAFF(perms);
    if (empChildren.length > 0) {
      items.push({ label: "Employees", icon: Briefcase, basePath: "/employees", children: empChildren });
    }
  }
  const acctChildren = ACCOUNTING_CHILDREN_FOR_STAFF(perms);
  if (acctChildren.length > 0) {
    items.push({ label: "Accounting", icon: Receipt, basePath: "/accounting", children: acctChildren });
  }
  if (perms.inventory_view) {
    items.push({ to: "/supplies", label: "Supplies & Inventory", icon: Package });
  }
  if (perms.events_view) {
    items.push({ to: "/events", label: "Events", icon: CalendarDays });
  }
  if (perms.parent_portal_view || perms.parent_portal_manage) {
    items.push({ to: "/parent-portal-settings", label: "Parent Portal", icon: UserCog });
  }
  items.push({ to: "/incidents", label: "Incident Reports", icon: ShieldAlert });
  items.push({ to: "/memos", label: "Memos", icon: Megaphone });
  items.push({ to: "/kpis", label: "KPI Targets", icon: Target });
  items.push({ to: "/notifications", label: "Notifications", icon: Bell });
  items.push({ to: "/change-password", label: "Change Password", icon: KeyRound });
  items.push({ to: "/help-center", label: "Help Center", icon: BookOpen });
  items.push({ to: "/help", label: "Help & Support", icon: LifeBuoy });
  return items;
}

const ACCOUNTING_CHILDREN = [
  { to: "/accounting/income", label: "Income", icon: ClipboardList },
  { to: "/accounting/expenses", label: "Expenses", icon: MinusCircle },
  { to: "/accounting/reports", label: "Reports", icon: FileText },
  { to: "/accounting/cash-analysis", label: "Cash Analysis", icon: Wallet },
  { to: "/accounting/daily-cash-report", label: "Daily Cash Report", icon: Printer },
];

const OWNER_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/school-profile", label: "My School Profile", icon: School },
  { to: "/landing-settings", label: "Landing Page", icon: Palette },
  { to: "/parent-portal-settings", label: "Parent Portal", icon: Users },
  { to: "/owner/sub-owners", label: "Sub-Owners", icon: ShieldCheck },
  { to: "/branches", label: "List of Branches", icon: Building2 },
  { to: "/classes", label: "Classes & Sections", icon: BookOpen },
  { to: "/branch-admins", label: "Branch Admins", icon: UserCog },
  { label: "Employees", icon: Briefcase, basePath: "/employees", children: EMPLOYEES_CHILDREN },
  { label: "Students", icon: Grad, basePath: "/students", children: STUDENTS_CHILDREN },
  { to: "/supplies", label: "Supplies & Inventories", icon: Package },
  { to: "/events", label: "Events", icon: CalendarDays },
  { label: "Accounting", icon: Receipt, basePath: "/accounting", children: ACCOUNTING_CHILDREN },
  { to: "/special-access", label: "Special Access", icon: KeyRound },
  { to: "/incidents", label: "Incident Reports", icon: ShieldAlert },
  { to: "/memos", label: "Memos", icon: Megaphone },
  { to: "/kpis", label: "KPI Targets", icon: Target },
  { to: "/insights", label: "AI Insights", icon: Brain },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/settings/telegram", label: "Telegram Alerts", icon: Send },
  { to: "/activity-logs", label: "Activity Logs", icon: ScrollText },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/change-password", label: "Change Password", icon: KeyRound },
  { to: "/help-center", label: "Help Center", icon: BookOpen },
  { to: "/help", label: "Help & Support", icon: LifeBuoy },
];

const ADMIN_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/schools-branches", label: "Schools & Branches", icon: Building },
  { to: "/admin/approvals", label: "Pending Approval", icon: ClipboardCheck },
  { to: "/admin/sub-admins", label: "Sub-Admins", icon: ShieldCheck },
  { to: "/admin/subscriptions", label: "Subscription Plans", icon: Crown },
  { to: "/admin/subscription-payments", label: "Payment Reviews", icon: CreditCard },
  { to: "/admin/insights", label: "AI Insights", icon: Brain },
  { to: "/admin/send-notification", label: "Send Notification", icon: Megaphone },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/settings/telegram", label: "Telegram Alerts", icon: Send },
  { to: "/change-password", label: "Change Password", icon: KeyRound },
  { to: "/help-center", label: "Help Center", icon: BookOpen },
  { to: "/help", label: "Help & Support", icon: LifeBuoy },
];

function kebab(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function LeafLink({ item, onNavigate, indent = false, badge = 0 }) {
  return (
    <NavLink
      to={item.to}
      end
      onClick={onNavigate}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 ${indent ? "pl-10 pr-3" : "px-3.5"} py-2.5 rounded-2xl text-sm font-body font-medium transition-all duration-200 ${
          isActive
            ? "bg-[#2563EB] text-white shadow-[0_4px_18px_-6px_rgba(37,99,235,0.50)]"
            : "text-slate-700 hover:bg-white/60 hover:text-[#1D4ED8]"
        }`
      }
      data-testid={`sidebar-link-${kebab(item.label)}`}
    >
      {({ isActive }) => (
        <>
          <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
          <span className="truncate flex-1">{item.label}</span>
          {badge > 0 && (
            <span className={`ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${isActive ? "bg-white text-[#2563EB]" : "bg-rose-500 text-white"}`} data-testid={`sidebar-badge-${kebab(item.label)}`}>
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function GroupItem({ item, onNavigate }) {
  const { pathname } = useLocation();
  const matchesGroup = pathname === item.basePath || pathname.startsWith(item.basePath + "/");
  // We derive `open` instead of mirroring `matchesGroup` into local state via
  // an effect. Once the user manually toggles the group, that intent sticks;
  // navigating into the matching path auto-expands without a re-render cycle.
  const [userToggle, setUserToggle] = useState(null); // null = follow matchesGroup, true/false = explicit
  const open = userToggle === null ? matchesGroup : userToggle;

  return (
    <div data-testid={`sidebar-group-${kebab(item.label)}`}>
      <button
        type="button"
        onClick={() => setUserToggle(!open)}
        className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-body font-medium transition-all duration-200 ${
          matchesGroup
            ? "bg-[#2563EB] text-white font-semibold shadow-[0_4px_18px_-6px_rgba(37,99,235,0.50)]"
            : "text-slate-700 hover:bg-white/60 hover:text-[#1D4ED8]"
        }`}
        aria-expanded={open}
        data-testid={`sidebar-group-toggle-${kebab(item.label)}`}
      >
        <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
        <span className="flex-1 text-left truncate">{item.label}</span>
        {open
          ? <ChevronDown className="w-4 h-4 shrink-0 opacity-70" />
          : <ChevronRight className="w-4 h-4 shrink-0 opacity-70" />}
      </button>

      {open && (
        <div className="mt-1 space-y-1" data-testid={`sidebar-submenu-${kebab(item.label)}`}>
          {item.children.map((c) => (
            <LeafLink key={c.to} item={c} onNavigate={onNavigate} indent />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ onNavigate }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navRef = useRef(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [inquiriesUnread, setInquiriesUnread] = useState(0);
  const [pendingSummary, setPendingSummary] = useState({});

  // Bug: Sidebar is mounted by every page's <DashboardLayout>, so each
  // navigation re-creates the nav DOM node with scrollTop=0. When the active
  // link sits below the fold (e.g. "Help & Support"), the user lands on a
  // page where their just-clicked item is invisible.
  //
  // Fix: after mount/pathname-change, directly set the nav's scrollTop so
  // the active link is centered (or at least visible). useLayoutEffect runs
  // synchronously after DOM mutations but before paint — no visible jump.
  // We use offsetTop / offsetHeight (relative to the offsetParent which IS
  // the nav itself thanks to overflow-y-auto creating a containing block)
  // because scrollIntoView() proved unreliable across browser engines when
  // the parent container is itself mounting in the same commit.
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector('a[aria-current="page"]');
    if (!active) return;
    const itemTop = active.offsetTop;
    const itemBottom = itemTop + active.offsetHeight;
    const navHeight = nav.clientHeight;
    if (itemBottom > navHeight) {
      // Bottom-bias by 24px so the active item never sits flush against the
      // edge — it should feel intentional, not "clipped at the bottom".
      nav.scrollTop = itemBottom - navHeight + 24;
    } else if (itemTop < nav.scrollTop) {
      nav.scrollTop = Math.max(0, itemTop - 24);
    }
  }, [pathname]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await api.get("/chat/unread-count");
        if (!cancelled) setChatUnread(res.data?.count || 0);
      } catch { /* ignore */ }
      try {
        const res = await api.get("/comms/pending-summary");
        if (!cancelled) setPendingSummary(res.data || {});
      } catch { /* ignore */ }
      try {
        const res = await api.get("/inquiries/unread-count");
        if (!cancelled) setInquiriesUnread(res.data?.count || 0);
      } catch { /* ignore — no perm or no inquiries yet */ }
    };
    refresh();
    const id = setInterval(refresh, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.id]);

  const incidentsBadge =
    (pendingSummary.incidents_pending_review || 0) +
    (pendingSummary.incidents_pending_share || 0) +
    (pendingSummary.incidents_to_ack || 0);
  const memosBadge = pendingSummary.memos_to_ack || 0;

  const badgeFor = (to) => {
    if (to === "/help") return chatUnread;
    if (to === "/incidents") return incidentsBadge;
    if (to === "/memos") return memosBadge;
    if (to === "/inquiries") return inquiriesUnread;
    return 0;
  };

  let items;
  if (user?.role === "super_admin" || user?.role === "sub_super_admin") {
    items = ADMIN_ITEMS;
  } else if (user?.role === "branch_admin" || user?.role === "special_access") {
    items = buildStaffItems(user.permissions || {});
  } else {
    items = OWNER_ITEMS;
  }

  return (
    <aside
      className="h-full flex flex-col bg-gradient-to-b from-[#EFF6FF] via-[#F5F8FF] to-[#E0EAFB] border-r border-[#2563EB]/10 w-72"
      data-testid="sidebar"
    >
      <div className="px-5 py-5 flex items-center justify-between">
        <NavLink to="/" className="flex items-center" onClick={onNavigate} data-testid="sidebar-logo">
          <img
            src="/brand/logo-day.png"
            alt="SchoolBoss"
            className="h-11 w-auto select-none"
            draggable="false"
          />
        </NavLink>
        {onNavigate && (
          <button onClick={onNavigate} className="lg:hidden text-brand-inkMuted" data-testid="sidebar-close-btn">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <nav ref={navRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {items.map((it) =>
          it.children
            ? <GroupItem key={it.label} item={it} onNavigate={onNavigate} />
            : <LeafLink key={it.to} item={it} onNavigate={onNavigate} badge={badgeFor(it.to)} />
        )}
      </nav>

      <div className="px-4 py-4 border-t border-[#2563EB]/10 bg-white/40 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <UserAvatar name={user?.name} src={user?.photo_base64} size="md" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-brand-inkMuted font-body">Logged in as</div>
            <div className="font-heading font-semibold text-[#1D4ED8] truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-[11px] text-brand-inkMuted capitalize truncate">{user?.role?.replace(/_/g, " ")}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
